import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Camera, Plus, Trash2, GripVertical, Briefcase, GraduationCap, Ruler, Heart, MapPin } from 'lucide-react';
import { User } from '../types';
interface EditProfileModalProps {
  user: User;
  onClose: () => void;
}
export function EditProfileModal({
  user,
  onClose
}: EditProfileModalProps) {
  const [name, setName] = useState(user.name);
  const [bio, setBio] = useState(user.bio);
  const [job, setJob] = useState(user.job);
  const [company, setCompany] = useState(user.company || '');
  const [education, setEducation] = useState(user.education || '');
  const [height, setHeight] = useState(user.height || '');
  const [lookingFor, setLookingFor] = useState(user.lookingFor || '');
  const [photos, setPhotos] = useState(user.photos);
  const [interests, setInterests] = useState(user.interests);
  const availableInterests = ['Travel', 'Music', 'Movies', 'Fitness', 'Food', 'Art', 'Reading', 'Gaming', 'Photography', 'Hiking', 'Yoga', 'Dancing', 'Cooking', 'Sports', 'Fashion', 'Tech'];
  const lookingForOptions = ['Relationship', 'Something casual', 'Marriage', 'Friends first', 'Not sure yet'];
  const toggleInterest = (interest: string) => {
    if (interests.includes(interest)) {
      setInterests(interests.filter(i => i !== interest));
    } else if (interests.length < 5) {
      setInterests([...interests, interest]);
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-134">
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="absolute right-0 top-0 bottom-0 w-full bg-white overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-135">
        {/* Header */}
        <div className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-zinc-100 px-4 py-4 flex items-center justify-between z-10" data-id="element-136">
          <div className="flex items-center gap-4" data-id="element-137">
            <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full transition-colors" data-id="element-138">
              <X size={24} className="text-zinc-600" data-id="element-139" />
            </button>
            <h1 className="text-xl font-bold text-zinc-900" data-id="element-140">Edit Profile</h1>
          </div>
          <button onClick={onClose} className="px-4 py-2 bg-gradient-to-r from-orange-500 to-rose-500 text-white rounded-full font-bold text-sm" data-id="element-141">
            Save
          </button>
        </div>

        <div className="p-4 space-y-6 pb-8" data-id="element-142">
          {/* Photos Section */}
          <div data-id="element-143">
            <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3 px-1" data-id="element-144">
              Photos
            </h2>
            <div className="grid grid-cols-3 gap-2" data-id="element-145">
              {[...Array(6)].map((_, index) => <div key={index} className={`aspect-[3/4] rounded-xl overflow-hidden relative ${photos[index] ? 'bg-zinc-200' : 'bg-zinc-100 border-2 border-dashed border-zinc-300'}`} data-id="element-146">
                  {photos[index] ? <>
                      <img src={photos[index]} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" data-id="element-147" />
                      <div className="absolute top-1 left-1 p-1 bg-black/30 rounded-full cursor-grab" data-id="element-148">
                        <GripVertical size={14} className="text-white" data-id="element-149" />
                      </div>
                      <button className="absolute top-1 right-1 p-1.5 bg-red-500 rounded-full" data-id="element-150">
                        <Trash2 size={12} className="text-white" data-id="element-151" />
                      </button>
                      {index === 0 && <div className="absolute bottom-1 left-1 px-2 py-0.5 bg-rose-500 rounded-full" data-id="element-152">
                          <span className="text-[10px] font-bold text-white" data-id="element-153">
                            MAIN
                          </span>
                        </div>}
                    </> : <button className="w-full h-full flex flex-col items-center justify-center text-zinc-400 hover:text-rose-500 transition-colors" data-id="element-154">
                      <Plus size={24} data-id="element-155" />
                      <span className="text-xs mt-1" data-id="element-156">Add</span>
                    </button>}
                </div>)}
            </div>
            <p className="text-xs text-zinc-500 mt-2 px-1" data-id="element-157">
              Drag to reorder. First photo is your main profile picture.
            </p>
          </div>

          {/* Bio */}
          <div data-id="element-158">
            <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3 px-1" data-id="element-159">
              About Me
            </h2>
            <textarea value={bio} onChange={e => setBio(e.target.value)} placeholder="Write something about yourself..." maxLength={500} className="w-full h-32 p-4 bg-zinc-50 rounded-2xl text-zinc-900 placeholder-zinc-400 resize-none focus:outline-none focus:ring-2 focus:ring-rose-500/20" data-id="element-160" />
            <p className="text-xs text-zinc-400 text-right mt-1" data-id="element-161">
              {bio.length}/500
            </p>
          </div>

          {/* Basic Info */}
          <div data-id="element-162">
            <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3 px-1" data-id="element-163">
              Basic Info
            </h2>
            <div className="space-y-3" data-id="element-164">
              <div className="flex items-center gap-3 p-4 bg-zinc-50 rounded-2xl" data-id="element-165">
                <Briefcase size={20} className="text-zinc-400" data-id="element-166" />
                <input type="text" value={job} onChange={e => setJob(e.target.value)} placeholder="Job Title" className="flex-1 bg-transparent text-zinc-900 placeholder-zinc-400 focus:outline-none" data-id="element-167" />
              </div>
              <div className="flex items-center gap-3 p-4 bg-zinc-50 rounded-2xl" data-id="element-168">
                <MapPin size={20} className="text-zinc-400" data-id="element-169" />
                <input type="text" value={company} onChange={e => setCompany(e.target.value)} placeholder="Company" className="flex-1 bg-transparent text-zinc-900 placeholder-zinc-400 focus:outline-none" data-id="element-170" />
              </div>
              <div className="flex items-center gap-3 p-4 bg-zinc-50 rounded-2xl" data-id="element-171">
                <GraduationCap size={20} className="text-zinc-400" data-id="element-172" />
                <input type="text" value={education} onChange={e => setEducation(e.target.value)} placeholder="Education" className="flex-1 bg-transparent text-zinc-900 placeholder-zinc-400 focus:outline-none" data-id="element-173" />
              </div>
              <div className="flex items-center gap-3 p-4 bg-zinc-50 rounded-2xl" data-id="element-174">
                <Ruler size={20} className="text-zinc-400" data-id="element-175" />
                <input type="text" value={height} onChange={e => setHeight(e.target.value)} placeholder="Height (e.g., 5'10&quot;)" className="flex-1 bg-transparent text-zinc-900 placeholder-zinc-400 focus:outline-none" data-id="element-176" />
              </div>
            </div>
          </div>

          {/* Looking For */}
          <div data-id="element-177">
            <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3 px-1" data-id="element-178">
              Looking For
            </h2>
            <div className="flex flex-wrap gap-2" data-id="element-179">
              {lookingForOptions.map(option => <button key={option} onClick={() => setLookingFor(option)} className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${lookingFor === option ? 'bg-gradient-to-r from-orange-500 to-rose-500 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`} data-id="element-180">
                  {option}
                </button>)}
            </div>
          </div>

          {/* Interests */}
          <div data-id="element-181">
            <div className="flex items-center justify-between mb-3 px-1" data-id="element-182">
              <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider" data-id="element-183">
                Interests
              </h2>
              <span className="text-xs text-zinc-400" data-id="element-184">
                {interests.length}/5
              </span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-185">
              {availableInterests.map(interest => <button key={interest} onClick={() => toggleInterest(interest)} className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${interests.includes(interest) ? 'bg-gradient-to-r from-orange-500 to-rose-500 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`} data-id="element-186">
                  {interest}
                </button>)}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}